<template>
  <div class="fixed top-0 left-0 w-full min-w-full h-full min-h-full">
    <transition name="fade">
      <LoadingView
        v-if="showLoadingView"
        :loading="loading"
        @close="closeLoadingView"
      />
    </transition>

    <transition name="fade">
      <div
        v-if="!showLoadingView"
        class="flex justify-center items-center w-full min-w-full h-full min-h-full"
      >
        <div class="flex bg-white max-w-5xl w-full shadow-lg overflow-hidden items-stretch">
          <div
            class="w-7/12 bg-center bg-cover"
            style="background-image: url('https://tools.playmore.golf/Compare/img/bg4.jpg')"
          />

          <div class="w-5/12 p-8 pl-9 pr-9">
            <div>
              <div class="text-2xl font-bold text-gray-800 heading leading-none">
                Club Membership Simulator
              </div>
              <div class="text-sm text-gray-500 mt-2 leading-relaxed">
                Personalize the membership platform simulator for a specific club
              </div>
            </div>

            <div class="mt-6">
              <div class="text-sm text-gray-700 font-semibold mt-2 leading-relaxed mb-1">
                Select a club
              </div>
              <div class="relative select-none">
                <div
                  class="flex justify-between items-center p-2 pl-3 pr-3 bg-gray-100 hover:bg-gray-200 cursor-pointer transition-all duration-500"
                  @click="clubDropdownClick()"
                >
                  <div class="text-sm text-gray-600">
                    <div v-if="selectedClub !== null">
                      {{ selectedClub.Name }}
                    </div>
                    <div
                      v-else
                      class="opacity-0 select-none"
                    >
                      -
                    </div>
                  </div>
                  <div v-if="!openClubDropdown">
                    <i class="far fa-chevron-down" />
                  </div>
                  <div v-else>
                    <i class="far fa-chevron-up" />
                  </div>
                </div>

                <div
                  v-if="openClubDropdown"
                  class="absolute top-0 left-0 mt-12 bg-gray-50 border w-full"
                >
                  <div class="border-b pl-3 pr-3 flex items-center leading-none w-full min-w-full">
                    <i class="far fa-search pt-2 pb-2 text-sm text-gray-500" />
                    <div class="w-full">
                      <input
                        v-model="searchClubName"
                        class="bg-transparent text-sm text-gray-800 outline-none p-2 pl-3 w-full"
                        placeholder="Search for a club..."
                      />
                    </div>
                  </div>

                  <div class="overflow-y-scroll max-h-48">
                    <div
                      v-for="club in filteredClubList"
                      class="flex justify-between items-center p-2 pl-3 pr-3 text-gray-600 hover:text-gray-800 cursor-pointer hover:bg-gray-200 transition-none duration-500 text-sm leading-none"
                      @click="selectClub(club)"
                    >
                      <div>{{ club.Name }}</div>
                      <div
                        class="text-sm text-gray-700"
                        :class="
                          selectedClub !== null && club.Name === selectedClub.Name
                            ? ''
                            : 'opacity-0'
                        "
                      >
                        <i class="far fa-check" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex mt-4">
              <div>
                <div
                  class="bg-white hover:bg-gray-100 transition-all duration-500 border w-4 h-4 leading-none cursor-pointer flex justify-center items-center"
                  @click="useLiveData = !useLiveData"
                >
                  <i
                    class="fas text-xs text-gray-600 fa-check"
                    :class="useLiveData ? '' : 'opacity-0'"
                    style="margin-top: 2px"
                  />
                </div>
              </div>
              <div class="ml-2 text-sm text-gray-500">
                <div class="text-gray-700 font-semibold leading-none">
                  Use live data
                </div>
                <div class="leading-none mt-1">
                  Gather live data values for this club
                </div>
              </div>
            </div>

            <div class="flex mt-4">
              <div>
                <div
                  class="bg-white hover:bg-gray-100 transition-all duration-500 border w-4 h-4 leading-none cursor-pointer flex justify-center items-center"
                  @click="advancedMode = !advancedMode"
                >
                  <i
                    class="fas text-xs text-gray-600 fa-check"
                    :class="advancedMode ? '' : 'opacity-0'"
                    style="margin-top: 2px"
                  />
                </div>
              </div>
              <div class="ml-2 text-sm text-gray-500">
                <div class="text-gray-700 font-semibold leading-none">
                  Advanced mode
                </div>
                <div class="leading-none mt-1">
                  This will display all fields for the calculator
                </div>
              </div>
            </div>

            <div class="mt-8">
              <router-link :to="'/Partner/0?showAllFields=' + advancedMode">
                <div
                  class="w-full min-w-full text-sm font-medium p-4 text-center duration-500 border select-none cursor-pointer uppercase text-gray-800 hover:text-gray-600 transition-all leading-none"
                >
                  Continue without a club
                </div>
              </router-link>

              <div
                class="w-full min-w-full text-sm font-medium p-4 text-center transition-all duration-500 text-white select-none uppercase border leading-none mt-2"
                :class="
                  selectedClub !== null
                    ? 'bg-secondary-100 hover:bg-secondary-200 border-secondary-100 cursor-pointer'
                    : 'bg-gray-300 text-gray-700 cursor-default'
                "
                @click="submit()"
              >
                View membership platform simulator
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import LoadingView from '@/app/shared/components/base/loading/loadingView.vue';
  import { AllClubs } from '@/app/shared/services/clubService/clubService';

  export default {
    name: 'ClubPartnerForm',

    components: {
      LoadingView,
    },

    data() {
      return {
        showLoadingView: true,
        clubs: [],
        selectedClub: null,
        advancedMode: true,
        useLiveData: true,

        searchClubName: '',
        openClubDropdown: false,
      };
    },

    computed: {
      filteredClubList() {
        return this.clubs.filter((c) =>
          c.Name.toLowerCase().includes(this.searchClubName.toLowerCase())
        );
      },

      loading() {
        if (this.clubs.length === 0) {
          return true;
        }
        return false;
      },
    },

    mounted() {
      this.getClubs();
    },

    methods: {
      selectClub(club) {
        this.selectedClub = club;
        this.openClubDropdown = false;
      },
      clubDropdownClick() {
        this.openClubDropdown = !this.openClubDropdown;
      },

      submit() {
        const id = this.selectedClub === null ? 0 : this.selectedClub.Id;
        this.$router.push(
          `/Partner/${id}?showAllFields=${this.advancedMode}&useLiveData=${this.useLiveData}`
        );
      },

      closeLoadingView() {
        this.showLoadingView = false;
      },

      getClubs() {
        AllClubs().then((data) => {
          this.clubs = data;
        });
      },
    },
  };
</script>
<style src="../assets/site.css"></style>
